/**
 * Utility functions for exporting data to Excel format
 */

export interface ExcelExportData {
  headers: string[];
  rows: (string | number)[][];
  title?: string;
}

/**
 * Converts data to CSV format and triggers download
 * @param data - The data to export
 * @param filename - The filename for the downloaded file
 */
export const exportToExcel = (data: ExcelExportData, filename: string): void => {
  const { headers, rows, title } = data;
  
  // Create CSV content
  let csvContent = '';
  
  // Add title if provided
  if (title) {
    csvContent += `"${title}"\n\n`;
  }
  
  // Add headers
  csvContent += headers.map(header => `"${header}"`).join(',') + '\n';
  
  // Add rows
  rows.forEach(row => {
    csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
  });
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Export industries data to Excel
 */
export const exportIndustriesToExcel = (industries: any[], jobTitle: string, socCode: string): void => {
  const headers = [
    'Industry Title',
    'Industry Code',
    'Industry Type',
    'Current Employment (2024)',
    'Projected Employment (2034)',
    'Employment Change (2024-2034)',
    'Employment % Change',
    '% of Industry (2024)',
    '% of Occupation (2024)',
    'Growth Status'
  ];
  
  const rows = industries.map(industry => [
    industry.industry_title || 'N/A',
    industry.industry_code || 'N/A',
    industry.industry_type || 'N/A',
    (industry.employment_2024 || industry.employment_2023 || industry.employment) ? Math.round(industry.employment_2024 || industry.employment_2023 || industry.employment || 0).toLocaleString() : 'N/A',
    (industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment) ? Math.round(industry.projected_employment_2034 || industry.projected_employment_2033 || industry.projected_employment || 0).toLocaleString() : 'N/A',
    industry.employment_change ? Math.round(industry.employment_change).toLocaleString() : 'N/A',
    industry.employment_percent_change ? `${industry.employment_percent_change.toFixed(1)}%` : 'N/A',
    (industry.percent_of_industry_2024 || industry.percent_of_industry_2023 || industry.percent_of_industry) ? `${(industry.percent_of_industry_2024 || industry.percent_of_industry_2023 || industry.percent_of_industry || 0).toFixed(2)}%` : 'N/A',
    (industry.percent_of_occupation_2024 || industry.percent_of_occupation_2023 || industry.percent_of_occupation) ? `${(industry.percent_of_occupation_2024 || industry.percent_of_occupation_2023 || industry.percent_of_occupation || 0).toFixed(2)}%` : 'N/A',
    industry.employment_change > 0 ? 'Growing' : industry.employment_change < 0 ? 'Declining' : 'Stable'
  ]);
  
  const title = `Industries for ${jobTitle} (SOC: ${socCode}) - Employment Projections 2024-2034`;
  const filename = `industries_${socCode}_${new Date().toISOString().split('T')[0]}.csv`;
  
  exportToExcel({ headers, rows, title }, filename);
};

/**
 * Export financial aid data to Excel
 */
export const exportFinancialAidToExcel = (financialAidData: any[]): void => {
  const headers = [
    'Program Name',
    'Category',
    'Type',
    'Amount',
    'Deadline',
    'Eligibility',
    'Description',
    'Website'
  ];
  
  const rows = financialAidData.map(item => [
    item.name || item.title || 'N/A',
    item.category || 'N/A',
    item.type || 'N/A',
    item.amount || 'N/A',
    item.deadline || 'N/A',
    item.eligibility || 'N/A',
    item.description || 'N/A',
    item.website || item.url || 'N/A'
  ]);
  
  const title = 'Financial Aid Programs and Resources';
  const filename = `financial_aid_${new Date().toISOString().split('T')[0]}.csv`;
  
  exportToExcel({ headers, rows, title }, filename);
};

/**
 * Export businesses data to Excel
 */
export const exportBusinessesToExcel = (businesses: any[], jobTitle: string, socCode: string): void => {
  const headers = [
    'Business Name',
    'Address',
    'City',
    'State',
    'Zip Code',
    'Phone',
    'Website',
    'Employees (2023)',
    'Industry'
  ];
  
  const rows = businesses.map(business => [
    business.name || 'N/A',
    business.address || 'N/A',
    business.city || 'N/A',
    business.state || 'N/A',
    business.zip || 'N/A',
    business.phone || 'N/A',
    business.website || 'N/A',
    business.employees_2023 ? Math.round(business.employees_2023).toLocaleString() : 'N/A',
    business.industry || 'N/A'
  ]);
  
  const title = `Businesses for ${jobTitle} (SOC: ${socCode})`;
  const filename = `businesses_${socCode}_${new Date().toISOString().split('T')[0]}.csv`;
  
  exportToExcel({ headers, rows, title }, filename);
};

/**
 * Export job postings data to Excel
 */
export const exportJobPostingsToExcel = (jobPostings: any[], jobTitle: string, socCode: string): void => {
  const headers = [
    'Job Title',
    'Company',
    'Location',
    'Distance (mi)',
    'Posted Date',
    'Federal Contractor',
    'Description',
    'URL'
  ];
  
  const rows = jobPostings.map(job => [
    job.JobTitle || 'N/A',
    job.Company || 'N/A',
    job.Location || 'N/A',
    job.Distance || 'N/A',
    job.AcquisitionDate ? new Date(job.AcquisitionDate).toLocaleDateString() : 'N/A',
    job.Fc ? 'Yes' : 'No',
    job.DescriptionSnippet || 'N/A',
    job.URL || 'N/A'
  ]);
  
  const title = `Job Postings for ${jobTitle} (SOC: ${socCode})`;
  const filename = `job_postings_${socCode}_${new Date().toISOString().split('T')[0]}.csv`;
  
  exportToExcel({ headers, rows, title }, filename);
};

























